import type { UserUpdateInput } from "../../../client/gql/graphql";

/** The account state the support actions branch on. */
export type AccountState = {
  userName: string;
  /** Profiles linked to this account — they survive an account deletion. */
  profileCount: number;
  emailVerified: boolean;
  invitePending: boolean;
  resetPending: boolean;
  newEmail?: string | null;
  /** uuids of the user's TOTP methods; empty means no 2FA. */
  twoFactorUuids: string[];
  /** Re-sending needs an active profile *and* an email address. */
  canResendInvitation: boolean;
  /**
   * No own login. A password is pointless, and re-inviting would flip the flag
   * back to false on the auth-server — so neither is offered.
   */
  impersonateOnly: boolean;
  /** A reset mail needs an address … */
  hasEmail: boolean;
  /** … and a local password: SSO accounts have none to reset. */
  federated: boolean;
};

export type AccountActionKey =
  | "setPassword"
  | "sendPasswordReset"
  | "resetTwoFactor"
  | "resendInvitation"
  | "revokeInvitation"
  | "revokePasswordReset"
  | "verifyEmail"
  | "cancelEmailChange"
  | "deleteAccount";

export type Confirmation = { title: string; message: string; confirmText: string };

/** The account-card row an action lives on — the row whose state it changes. */
export type AccountRow =
  | "email"
  | "emailChange"
  | "invitation"
  | "password"
  | "twoFactor"
  | "danger";

export type AccountAction = {
  key: AccountActionKey;
  /** Items sharing a group are listed together; a ruler separates the groups. */
  group: number;
  label: string;
  /** Where the action is offered on the account card. */
  row: AccountRow;
  /** Short label for the row button ("Widerrufen"); `label` is the full one. */
  rowLabel: string;
  color?: "error";
  confirm?: Confirmation;
  /** `updateUsers` payload. Absent for actions that open a modal or delete. */
  update?: UserUpdateInput;
  /**
   * Runs one of the dedicated revoke mutations instead of `updateUsers`: the
   * tokens are @private, and a token whose expiry alone is nulled never expires.
   */
  revoke?: "invitation" | "passwordReset";
  /** Calls an auth-server endpoint instead of the PIM backend. */
  remote?: "sendPasswordReset";
  /** Toast shown after a successful `update`. */
  success?: string;
};

/** Copy and payload of one action, without its place in the menu. */
export type AccountFix = Required<Pick<AccountAction, "label" | "confirm" | "update" | "success">>;

/**
 * The auth-server has no admin endpoint for re-sending a plain confirmation
 * mail, so marking it verified is the shortcut support actually has (next to
 * re-sending the invitation).
 */
export const VERIFY_EMAIL: AccountFix = {
  label: "E-Mail als bestätigt markieren",
  confirm: {
    title: "E-Mail als bestätigt markieren",
    message:
      "Die Bestätigung per E-Mail wird übersprungen. Nur verwenden, wenn die Adresse auf anderem Weg verifiziert wurde.",
    confirmText: "Bestätigen",
  },
  update: { emailVerified: true },
  success: "E-Mail als bestätigt markiert",
};

/**
 * `deleteUsers` removes the User node only — the connected Profiles stay and
 * show up as "Kein Konto" afterwards. Say so instead of promising a cascade.
 */
export function deleteAccountConfirm(userName: string, profileCount: number): Confirmation {
  const head = `Das Login-Konto von „${userName}" wird unwiderruflich gelöscht.`;
  const tail =
    profileCount === 1
      ? ' Das verknüpfte Profil bleibt bestehen und erscheint danach als „Kein Konto".'
      : ` Die ${profileCount} verknüpften Profile bleiben bestehen und erscheinen danach als „Kein Konto".`;
  return {
    title: "Konto löschen",
    message: profileCount > 0 ? head + tail : head,
    confirmText: "Löschen",
  };
}

/** The support actions offered for an account, in row/menu order. */
export function accountActions(state: AccountState): AccountAction[] {
  const actions: AccountAction[] = [];
  if (!state.impersonateOnly) {
    actions.push({
      key: "setPassword",
      group: 0,
      row: "password",
      rowLabel: "Passwort setzen",
      label: "Passwort setzen",
    });
  }
  if (!state.impersonateOnly && state.hasEmail && !state.federated) {
    actions.push({
      key: "sendPasswordReset",
      group: 0,
      row: "password",
      rowLabel: "Reset-Link senden",
      label: "Reset-Link senden",
      remote: "sendPasswordReset",
      confirm: {
        title: "Reset-Link senden",
        message: `„${state.userName}" erhält per E-Mail einen Link zum Zurücksetzen des Passworts, sechs Stunden gültig. Eine offene Einladung wird damit hinfällig.`,
        confirmText: "Senden",
      },
      success: "Reset-Link gesendet",
    });
  }

  if (state.twoFactorUuids.length > 0) {
    actions.push({
      key: "resetTwoFactor",
      group: 0,
      row: "twoFactor",
      rowLabel: "Zurücksetzen",
      label: "2FA zurücksetzen",
      confirm: {
        title: "2FA zurücksetzen",
        message: `Alle hinterlegten 2FA-Methoden von „${state.userName}" werden entfernt. Die Anmeldung ist danach wieder allein mit Passwort möglich.`,
        confirmText: "Zurücksetzen",
      },
      update: {
        twoFactorMethods: [
          { delete: state.twoFactorUuids.map((uuid) => ({ where: { node: { uuid } } })) },
        ],
      },
      success: "2FA zurückgesetzt",
    });
  }

  // Re-inviting a confirmed, active account would only send an "access
  // granted" mail — offered only while the sign-up is actually unfinished.
  if (
    state.canResendInvitation &&
    !state.impersonateOnly &&
    (state.invitePending || !state.emailVerified)
  ) {
    // With an open invitation this is that row's action; without one it is the
    // fix for an unconfirmed address and sits on the e-mail row.
    actions.push(
      state.invitePending
        ? {
            key: "resendInvitation",
            group: 1,
            row: "invitation",
            rowLabel: "Erneut senden",
            label: "Einladung erneut senden",
          }
        : {
            key: "resendInvitation",
            group: 1,
            row: "email",
            rowLabel: "Einladung erneut senden",
            label: "Einladung erneut senden",
          },
    );
  }
  if (state.invitePending) {
    actions.push({
      key: "revokeInvitation",
      group: 1,
      row: "invitation",
      rowLabel: "Widerrufen",
      label: "Einladung widerrufen",
      confirm: {
        title: "Einladung widerrufen",
        message: `Der offene Einladungslink von „${state.userName}" wird ungültig. Eine neue Einladung kann jederzeit gesendet werden.`,
        confirmText: "Widerrufen",
      },
      revoke: "invitation",
      success: "Einladung widerrufen",
    });
  }
  if (state.resetPending) {
    actions.push({
      key: "revokePasswordReset",
      group: 1,
      row: "password",
      rowLabel: "Reset widerrufen",
      label: "Passwort-Reset widerrufen",
      confirm: {
        title: "Passwort-Reset widerrufen",
        message: `Der offene Reset-Link von „${state.userName}" wird ungültig. Das bestehende Passwort bleibt gültig.`,
        confirmText: "Widerrufen",
      },
      revoke: "passwordReset",
      success: "Passwort-Reset widerrufen",
    });
  }

  if (!state.emailVerified) {
    actions.push({
      key: "verifyEmail",
      group: 2,
      row: "email",
      rowLabel: "Als bestätigt markieren",
      ...VERIFY_EMAIL,
    });
  }
  if (state.newEmail) {
    actions.push({
      key: "cancelEmailChange",
      group: 2,
      row: "emailChange",
      rowLabel: "Abbrechen",
      label: "E-Mail-Wechsel abbrechen",
      confirm: {
        title: "E-Mail-Wechsel abbrechen",
        message: `Der ausstehende Wechsel auf ${state.newEmail} wird verworfen. Die bisherige Login-Adresse bleibt bestehen.`,
        confirmText: "Verwerfen",
      },
      update: { newEmail: null, newEmailOtpValidUntil: null },
      success: "E-Mail-Wechsel abgebrochen",
    });
  }

  actions.push({
    key: "deleteAccount",
    group: 3,
    row: "danger",
    rowLabel: "Konto löschen",
    label: "Konto löschen",
    color: "error",
    confirm: deleteAccountConfirm(state.userName, state.profileCount),
  });

  return actions;
}
